import { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { Settings as SettingsIcon, Menu as MenuIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SettingsProvider, useClientSettings } from '../client-pwa/settings';
import SplashV3 from './SplashV3';

const navItems = [
  { to: '/client-pwa-v3', label: 'Today', section: '01', end: true },
  { to: '/client-pwa-v3/files', label: 'Records', section: '02' },
  { to: '/client-pwa-v3/insights', label: 'Analysis', section: '03' },
  { to: '/client-pwa-v3/help', label: 'Ask AI', section: '04' },
  { to: '/client-pwa-v3/messages', label: 'Messages', section: '05' },
];

function ShellV3() {
  const { settings, themeMode } = useClientSettings();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const onSettingsPage = location.pathname.includes('/settings');

  // Resolve current section for the slim header
  const currentItem = [...navItems].reverse().find(item =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
  ) || navItems[0];

  const dotPattern = themeMode === 'dark'
    ? 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)'
    : 'radial-gradient(rgba(0,0,0,0.025) 1px, transparent 1px)';

  return (
    <div
      className={cn(
        'min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100',
        settings.largeText && 'text-[110%]',
      )}
      style={{
        backgroundImage: dotPattern,
        backgroundSize: '24px 24px',
      }}
    >
      <SplashV3 />

      <div className="mx-auto flex min-h-screen w-full max-w-[680px] flex-col">
        {/* Slim sticky header - single bar, ~52px tall */}
        {!onSettingsPage && (
          <header className="sticky top-0 z-20 border-b border-stone-300 dark:border-stone-700 bg-stone-50/95 dark:bg-stone-950/95 backdrop-blur-md">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Menu button */}
              <button
                onClick={() => setMenuOpen(true)}
                className="flex h-9 w-9 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:border-stone-500"
                aria-label="Open menu"
              >
                <MenuIcon className="h-4 w-4" />
              </button>

              {/* Centered: section number + current page title */}
              <div className="flex flex-1 items-center justify-center gap-2">
                <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ {currentItem.section}</span>
                <span className="text-[12px] text-stone-300 dark:text-stone-600">·</span>
                <h1 className="font-serif text-base font-bold tracking-tight">{currentItem.label}</h1>
              </div>

              {/* Settings */}
              <Link
                to="/client-pwa-v3/settings"
                className="flex h-9 w-9 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:border-stone-500"
              >
                <SettingsIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          </header>
        )}

        <main className="flex-1 px-5 pb-12">
          <Outlet />
        </main>
      </div>

      {/* Slide-out menu drawer */}
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            className="fixed inset-0 z-40 bg-stone-900/30 dark:bg-stone-50/30 backdrop-blur-sm animate-in fade-in duration-200"
          />
          <aside
            className={cn(
              'fixed top-0 z-50 flex h-full w-[300px] max-w-[85%] flex-col bg-stone-50 dark:bg-stone-950 shadow-2xl animate-in duration-300',
              'left-0 border-r border-stone-300 dark:border-stone-700 slide-in-from-left'
            )}
          >
            {/* Drawer masthead */}
            <div className="border-b border-stone-300 dark:border-stone-700 px-5 py-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 text-xs font-bold tracking-widest text-stone-50">
                    VA
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-900 dark:text-stone-100">Beacon Accounting</p>
                    <p className="text-[11px] uppercase tracking-wider text-stone-500">Vol IV · No 17</p>
                  </div>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="flex h-8 w-8 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:border-stone-500"
                  aria-label="Close menu"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mt-5 border-t border-stone-300 dark:border-stone-700 pt-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-stone-500">
                  James Tiling Pty Ltd
                </p>
                <h2 className="mt-1 font-serif text-2xl font-bold leading-none tracking-tight">
                  The Ledger
                </h2>
                <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">
                  Mon · 28 April 2026
                </p>
              </div>
            </div>

            {/* Nav list */}
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              <p className="mb-2 px-2 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-stone-500">
                Sections
              </p>
              <div className="space-y-0.5">
                {navItems.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 border-l-2 px-3 py-3 text-sm transition-colors',
                        isActive
                          ? 'border-teal-700 dark:border-teal-500 bg-white dark:bg-stone-900 font-bold text-stone-900 dark:text-stone-100'
                          : 'border-transparent text-stone-600 dark:text-stone-400 hover:border-stone-400 hover:bg-white'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={cn('font-mono text-[12px]', isActive ? 'text-teal-700 dark:text-teal-400' : 'text-stone-400 dark:text-stone-500')}>
                          § {item.section}
                        </span>
                        <span className={cn('font-serif text-base', !isActive && 'font-normal')}>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>

              <div className="mt-6 border-t border-stone-200 dark:border-stone-800 pt-4">
                <p className="mb-2 px-2 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-stone-500">
                  Account
                </p>
                <Link
                  to="/client-pwa-v3/settings"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 border-l-2 border-transparent px-3 py-3 text-sm text-stone-600 dark:text-stone-400 hover:border-stone-400 hover:bg-white"
                >
                  <SettingsIcon className="h-4 w-4" />
                  <span className="font-serif text-base">Settings</span>
                </Link>
              </div>
            </nav>

            {/* Drawer footer - colophon */}
            <div className="border-t border-stone-300 dark:border-stone-700 px-5 py-5 text-center">
              <p className="font-serif text-[13px] italic text-stone-500">
                James Halloran · Director
              </p>
              <div className="mt-3 border-t border-stone-200 dark:border-stone-800 pt-3">
                <p className="font-serif text-[12px] italic text-stone-400 dark:text-stone-500">
                  Compiled with care by Beacon Accounting
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.3em] text-stone-400 dark:text-stone-500">
                  Powered by <span className="font-bold text-stone-700 dark:text-stone-300">RoniDoc</span>
                </p>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

export default function ClientPwaV3Layout() {
  return (
    <SettingsProvider>
      <ShellV3 />
    </SettingsProvider>
  );
}
